import { defineMessages } from 'react-intl';

import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';

type MessageKeys =
	| 'cancelText'
	| 'confirmText'
	| 'title'
	| 'body'
	| 'error'
	| 'agentDeletedSuccessFlagTitle'
	| 'agentDeletedSuccessFlagDescription'
	| 'agentDeletedErrorFlagTitle'
	| 'agentDeletedErrorFlagDescription'
	| typeof ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED
	| typeof ASSISTANCE_SERVICE_ERROR_CODES.AGENT_HAS_CONVERSATIONS
	| typeof ASSISTANCE_SERVICE_ERROR_CODES.USER_NOT_CREATOR;

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		cancelText: {
			id: 'ai-mate.agent-modal.agent-delete-confirm.cancel-text',
			defaultMessage: 'Cancel',
			description: 'Cancel button label in the agent deletion confirmation modal',
		},
		confirmText: {
			id: 'ai-mate.agent-modal.agent-delete-confirm.confirm-text',
			defaultMessage: 'Delete',
			description: 'Confirm button label in the agent deletion confirmation modal',
		},
		title: {
			id: 'ai-mate.agent-list.delete-agent-confirm-title',
			defaultMessage: 'Delete "{agentName}"?',
			description: 'Title text for the delete agent confirmation modal',
		},
		body: {
			id: 'ai-mate.agent-list.delete-agent-confirm-body',
			defaultMessage: `Are you sure you want to delete this agent? This action cannot be undone.`,
			description: 'Body text for the delete agent confirmation modal',
		},
		error: {
			id: 'ai-mate.agent-list.delete-agent-error',
			defaultMessage: 'Could not delete agent',
			description: 'Error message displayed when an agent cannot be deleted',
		},
		agentDeletedSuccessFlagTitle: {
			id: 'ai-mate.agent-list.delete-agent-success-flag-title',
			defaultMessage: 'Agent deleted',
			description: 'Flag message title displayed when an agent is deleted',
		},
		agentDeletedSuccessFlagDescription: {
			id: 'ai-mate.agent-list.delete-agent-success-flag-description',
			defaultMessage: 'You deleted {agentName}',
			description: 'Flag message description displayed when an agent is deleted',
		},
		agentDeletedErrorFlagTitle: {
			id: 'ai-mate.agent-list.delete-agent-error-flag-title',
			defaultMessage: 'Failed to delete agent',
			description: 'Flag message title displayed when there is an error deleting agent',
		},
		agentDeletedErrorFlagDescription: {
			id: 'ai-mate.agent-list.delete-agent-error-flag-description',
			defaultMessage: 'Something went wrong while deleting the agent. Try again in a few moments.',
			description: 'Flag message description displayed when there is an error deleting agent',
		},
		[ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED]: {
			id: 'ai-mate.agent-list.delete-agent-error.agent-not-found',
			defaultMessage: 'That agent could not be found.',
			description: 'Error message displayed when an agent cannot be found',
		},
		[ASSISTANCE_SERVICE_ERROR_CODES.AGENT_HAS_CONVERSATIONS]: {
			id: 'ai-mate.agent-list.delete-agent-error.agent-in-use',
			defaultMessage: 'Agent is currently in use. Try again later.',
			description: 'Error message displayed when an agent is in use',
		},
		[ASSISTANCE_SERVICE_ERROR_CODES.USER_NOT_CREATOR]: {
			id: 'ai-mate.agent-list.delete-agent-error.incorrect-owner',
			defaultMessage:
				'You cannot delete agents you have not created yourself. Please contact the agent owner.',
			description: 'Error message displayed when the agent owner is incorrect',
		},
	});

export default message;
